import { z } from 'zod'

const publicEnvSchema = z
  .object({
    VITE_SUPABASE_URL: z.string().url(),
    VITE_SUPABASE_PUBLISHABLE_KEY: z.string().min(20).optional(),
    VITE_SUPABASE_ANON_KEY: z.string().min(20).optional(),
    VITE_APP_ENV: z.enum(['development', 'staging', 'production']).default('development'),
  })
  .refine(
    (value) => Boolean(value.VITE_SUPABASE_PUBLISHABLE_KEY || value.VITE_SUPABASE_ANON_KEY),
    {
      message: 'A Supabase publishable/anon key is required',
      path: ['VITE_SUPABASE_PUBLISHABLE_KEY'],
    },
  )

export type PublicEnv = z.infer<typeof publicEnvSchema> & {
  supabasePublicKey: string
}

let cachedEnv: PublicEnv | undefined

export function getPublicEnv(): PublicEnv {
  if (cachedEnv) {
    return cachedEnv
  }

  const parsed = publicEnvSchema.safeParse(import.meta.env)

  if (!parsed.success) {
    const details = parsed.error.issues
      .map((issue) => `${issue.path.join('.')}: ${issue.message}`)
      .join('; ')

    throw new Error(`Invalid browser environment configuration: ${details}`)
  }

  const supabasePublicKey =
    parsed.data.VITE_SUPABASE_PUBLISHABLE_KEY ?? parsed.data.VITE_SUPABASE_ANON_KEY

  if (!supabasePublicKey) {
    throw new Error('Supabase browser key is required.')
  }

  cachedEnv = {
    ...parsed.data,
    supabasePublicKey,
  }

  return cachedEnv
}
